"use client"

import { paymentFields } from "@/actions/payment/client/payment-fields"
import {
    AddressFormEnum,
    type AddressFormType
} from "@/app/(site)/[locale]/checkout/CheckoutContent/Steps/CustomerInformation/CustomerFormFieldGroup/AddressForm"
import Select from "@/app/(site)/_components/Atoms/Selects/select"
import { useSWRBasketSessionState } from "@/app/(site)/_utils/_hooks/SWR/useSWRBasketSessionState"
import { useGetSameShippingAddress } from "@/app/(site)/_utils/_hooks/useGetSameShippingAddress"
import { usePfasValidation } from "@/app/(site)/_utils/_hooks/usePfasValidation"
import { type CheckoutAddress, useCheckoutFormStore } from "@/app/(site)/contexts/Global"
import { type IPaymentQueryParams } from "@/lib/centra-client/interfaces/IPaymentService"
import { USER_HAS_SELECTED_STATE_STORAGE_KEY } from "@/lib/siteconfig/constants"
import { t } from "@/translations"
import { useCallback, useMemo, useState } from "react"

/**
 * Minimal shape of an already-committed Centra selection address. Only the fields the tax
 * calculation needs to carry over are listed; the live selection objects are structurally
 * compatible and may carry more.
 */
type CommittedAddress = {
    firstName?: string | null
    lastName?: string | null
    address1?: string | null
    phoneNumber?: string | null
    zipCode?: string | null
    email?: string | null
    city?: string | null
    country?: string | null
    state?: string | null
}

interface SelectCountryStateProps {
    value?: string
    disabled?: boolean
    label?: string
    name: string
    required?: boolean
    selectedCountryCode?: string
    zipCode?: string
    countries?: Array<{
        country: string
        name: string
        states?: { state: string; name: string }[]
    }>
    langCode: string
    onChange?: (e: React.ChangeEvent<HTMLSelectElement>) => void
    labelClassName?: string
    inputClassName?: string
    containerClassName?: string
    prefix?: string
    isLoading?: boolean
    placeholder?: string
    onBlurValidation?: boolean
    // Tax calculation props
    formData?: {
        firstName?: string
        lastName?: string
        address1?: string
        apartmentHouseNumber?: string
        address2?: string
        phoneNumber?: string
        email?: string
        city?: string
        country?: string
    }
    onTaxCalculationStart?: () => void
    onTaxCalculationComplete?: (result: any) => void
    tabIndex?: number
    autoComplete?: string
    disablePfasValidation?: boolean
    /**
     * Which address this state selector belongs to. Determines which slot of the
     * `paymentFields` payload is driven by the live form vs. carried over unchanged, so a
     * billing-side tax calc never overwrites the shipping address (and vice versa).
     */
    addressType: AddressFormType
}

interface SelectOption {
    value: string
    label: string
}

export default function SelectCountryState({
    value,
    disabled = false,
    isLoading = false,
    label,
    name,
    required = false,
    selectedCountryCode = "",
    zipCode = "",
    countries = [],
    langCode,
    onChange,
    labelClassName,
    inputClassName,
    containerClassName,
    prefix = "",
    onBlurValidation = false,
    placeholder = "",
    formData,
    onTaxCalculationStart,
    onTaxCalculationComplete,
    tabIndex,
    autoComplete = "",
    disablePfasValidation = false,
    addressType,
    ...props
}: SelectCountryStateProps) {
    const [isCalculatingTax, setIsCalculatingTax] = useState(false)
    const { mutate, selection } = useSWRBasketSessionState()
    const billingAddress = useCheckoutFormStore(state => state.billingAddress)
    const shippingAddress = useCheckoutFormStore(state => state.shippingAddress)
    const sameShippingAddress = useGetSameShippingAddress()
    // PFAS validation for US country
    const { isPfasRestricted } = usePfasValidation({
        country: selectedCountryCode,
        state: value,
        zipCode,
        disabled: disablePfasValidation
    })

    // these two callbacks needs to check if we are to pre-fill the state for the user or not
    // Poc wants this empty initially but we need to set state in order to call /payment in CheckoutFlow
    const getUserHasSelectedState = useCallback(() => {
        if (typeof window === "undefined") {
            return false
        }
        return sessionStorage.getItem(USER_HAS_SELECTED_STATE_STORAGE_KEY) === "true"
    }, [])

    const displayValue = useMemo(() => {
        const userHasSelected = getUserHasSelectedState()
        return userHasSelected && value ? value : ""
    }, [value, getUserHasSelectedState])

    // -----END OF HAS USER SELECTED STATE-----

    const formDataKey = JSON.stringify(formData)

    // Tax calculation function
    const calculateTax = useCallback(
        async (selectedState: string) => {
            // Only calculate tax if we have zipcode and state
            const hasZipCode = zipCode && zipCode.trim() !== ""
            const hasState = selectedState && selectedState.trim() !== ""

            if (!hasZipCode || !hasState || !formData) {
                return
            }

            setIsCalculatingTax(true)
            onTaxCalculationStart?.()

            try {
                // Built from the live form fields plus the country/state the user just picked.
                const buildFormAddress = (
                    committed: CommittedAddress | null | undefined,
                    storeAddr: CheckoutAddress
                ) => ({
                    firstName:
                        formData.firstName ||
                        committed?.firstName ||
                        storeAddr.firstName ||
                        null,
                    lastName:
                        formData.lastName ||
                        committed?.lastName ||
                        storeAddr.lastName ||
                        null,
                    address1: formData.apartmentHouseNumber
                        ? `${formData.address1} ${formData.apartmentHouseNumber}`
                        : formData.address1 ||
                          committed?.address1 ||
                          storeAddr.address1 ||
                          null,
                    address2: formData.address2 || storeAddr.address2 || null,
                    phoneNumber:
                        formData.phoneNumber ||
                        committed?.phoneNumber ||
                        storeAddr.phoneNumber ||
                        null,
                    zipCode: zipCode || committed?.zipCode || storeAddr.zipCode || null,
                    email: formData.email || committed?.email || storeAddr.email || null,
                    city: formData.city || committed?.city || storeAddr.city || null,
                    country:
                        selectedCountryCode ||
                        committed?.country ||
                        storeAddr.country ||
                        null,
                    state: selectedState || committed?.state || storeAddr.state || null
                })

                // Carried over from already-committed data ONLY — never the live form. This is
                // what keeps the address the user is NOT editing intact, so a billing-side tax
                // calc never overwrites the shipping address (and vice versa).
                const buildCommittedAddress = (
                    committed: CommittedAddress | null | undefined,
                    storeAddr: CheckoutAddress
                ) => ({
                    firstName: committed?.firstName || storeAddr.firstName || null,
                    lastName: committed?.lastName || storeAddr.lastName || null,
                    address1: committed?.address1 || storeAddr.address1 || null,
                    address2: storeAddr.address2 || null,
                    phoneNumber: committed?.phoneNumber || storeAddr.phoneNumber || null,
                    zipCode: committed?.zipCode || storeAddr.zipCode || null,
                    email: committed?.email || storeAddr.email || null,
                    city: committed?.city || storeAddr.city || null,
                    country: committed?.country || storeAddr.country || null,
                    state: committed?.state || storeAddr.state || null
                })

                const ownIsBilling = addressType === AddressFormEnum.BILLING_ADDRESS

                // When billing mirrors shipping, the single (shipping) form drives both slots.
                const billingAddressPayload =
                    ownIsBilling || sameShippingAddress
                        ? buildFormAddress(selection?.address, billingAddress)
                        : buildCommittedAddress(selection?.address, billingAddress)

                const shippingAddressPayload = ownIsBilling
                    ? buildCommittedAddress(selection?.shippingAddress, shippingAddress)
                    : buildFormAddress(selection?.shippingAddress, shippingAddress)

                const paymentFieldsData: IPaymentQueryParams = {
                    language: langCode,
                    address: billingAddressPayload,
                    shippingAddress: shippingAddressPayload
                }

                const result = await paymentFields(paymentFieldsData)

                if (result._tag === "success") {
                    onTaxCalculationComplete?.(result.data)
                    await mutate()
                    return
                } else {
                    console.error("Tax calculation failed:", result.error)
                }
            } catch (error) {
                console.error("Error calculating tax:", error)
            } finally {
                setIsCalculatingTax(false)
            }
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [zipCode, formDataKey, selectedCountryCode, addressType, sameShippingAddress]
    )

    // Handle state selection with tax calculation
    const handleStateChange = useCallback(
        (e: React.ChangeEvent<HTMLSelectElement>) => {
            const selectedState = e.target.value
            if (selectedState && typeof window !== "undefined") {
                sessionStorage.setItem(USER_HAS_SELECTED_STATE_STORAGE_KEY, "true")
            }
            onChange?.(e)

            // Trigger tax calculation if we have zipcode and the selected state
            if (
                zipCode &&
                zipCode.trim() !== "" &&
                selectedState &&
                selectedState.trim() !== ""
            ) {
                calculateTax(selectedState)
            }
        },
        [onChange, zipCode, calculateTax]
    )

    switch (selectedCountryCode.toLocaleLowerCase()) {
        case "it":
            label = t(langCode, "checkout.information_for_billing_and_shipping.province")
            break
        case "gb":
            label = t(langCode, "checkout.information_for_billing_and_shipping.county")
            break
        case "jp":
            label = t(
                langCode,
                "checkout.information_for_billing_and_shipping.prefecture"
            )
            break
        case "au":
            label = t(
                langCode,
                "checkout.information_for_billing_and_shipping.state_territory"
            )
            break
        case "ca":
            label = t(langCode, "checkout.information_for_billing_and_shipping.province")
            break
        default:
            label = t(langCode, "checkout.information_for_billing_and_shipping.state") // default to state
    }
    const stateOptions: SelectOption[] = useMemo(() => {
        if (!selectedCountryCode || !countries.length) {
            return []
        }
        const selectedCountry = countries.find(
            country => country.country === selectedCountryCode
        )
        const states = selectedCountry?.states || []
        return states.map(state => ({
            value: state.state,
            label: state.name
        }))
    }, [selectedCountryCode, countries])

    return (
        <Select
            options={stateOptions}
            onChange={handleStateChange}
            name={name}
            langCode={langCode}
            isLoading={isLoading}
            label={label}
            labelClassName={labelClassName}
            inputClassName={inputClassName}
            containerClassName={containerClassName}
            /** A restriction on the basket, not on the state the customer picked. */
            externalErrors={isPfasRestricted ? ["pfas_restricted"] : []}
            placeholder={placeholder}
            required={required}
            prefix={prefix}
            value={displayValue}
            id="state-selector"
            onBlurValidation={onBlurValidation}
            tabIndex={tabIndex}
            autoComplete={autoComplete}
            {...props}
        />
    )
}
